import { createSlice } from '@reduxjs/toolkit';

import {
  name,
  setAutoplayAsync,
  setNextTitleAsync,
  getAuthProvidersAsync,
  linkAppleAsync,
  linkFacebookAsync,
  linkGoogleAsync,
  linkTwitterAsync,
  setPasswordByInvitationTokenAsync,
  registerToBusinessGroupAsync,
  getUserCountryAsync,
  onboardingCompletedAsync,
  addOnboardingQuestionnaireAsync,
} from './thunks';
import { AccountState } from './types';

const initialState: AccountState = {
  setAutoplay: { status: 'idle' },
  setNextTitle: { status: 'idle' },
  authProviders: { data: [], status: 'idle' },
  linkApple: { status: 'idle' },
  linkFacebook: { status: 'idle' },
  linkGoogle: { status: 'idle' },
  linkTwitter: { status: 'idle' },
  setPasswordByInvitationToken: {
    status: 'idle',
  },
  registerToBusinessGroup: {
    status: 'idle',
  },
  getCountry: {
    data: 'JO',
    status: 'idle',
  },
  onboardingCompleted: {
    status: 'idle',
  },
  onboardingQuestionnaire: {
    status: 'idle',
  },
};

export const slice = createSlice({
  name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setAutoplayAsync.pending, (state) => {
        state.setAutoplay.status = 'loading';
      })
      .addCase(setAutoplayAsync.fulfilled, (state) => {
        state.setAutoplay.status = 'idle';
      })
      .addCase(setAutoplayAsync.rejected, (state) => {
        state.setAutoplay.status = 'failed';
      })
      .addCase(setNextTitleAsync.pending, (state) => {
        state.setNextTitle.status = 'loading';
      })
      .addCase(setNextTitleAsync.fulfilled, (state) => {
        state.setNextTitle.status = 'idle';
      })
      .addCase(setNextTitleAsync.rejected, (state) => {
        state.setNextTitle.status = 'failed';
      })
      .addCase(getAuthProvidersAsync.pending, (state) => {
        state.authProviders.status = 'loading';
      })
      .addCase(getAuthProvidersAsync.fulfilled, (state, action) => {
        state.authProviders.status = 'idle';
        state.authProviders.data = action.payload;
      })
      .addCase(getAuthProvidersAsync.rejected, (state) => {
        state.authProviders.status = 'failed';
      })
      .addCase(linkAppleAsync.pending, (state) => {
        state.linkApple.status = 'loading';
      })
      .addCase(linkAppleAsync.fulfilled, (state) => {
        state.linkApple.status = 'success';
      })
      .addCase(linkAppleAsync.rejected, (state) => {
        state.linkApple.status = 'failed';
      })
      .addCase(linkFacebookAsync.pending, (state) => {
        state.linkFacebook.status = 'loading';
      })
      .addCase(linkFacebookAsync.fulfilled, (state) => {
        state.linkFacebook.status = 'success';
      })
      .addCase(linkFacebookAsync.rejected, (state) => {
        state.linkFacebook.status = 'failed';
      })
      .addCase(linkGoogleAsync.pending, (state) => {
        state.linkGoogle.status = 'loading';
      })
      .addCase(linkGoogleAsync.fulfilled, (state) => {
        state.linkGoogle.status = 'success';
      })
      .addCase(linkGoogleAsync.rejected, (state) => {
        state.linkGoogle.status = 'failed';
      })
      .addCase(linkTwitterAsync.pending, (state) => {
        state.linkTwitter.status = 'loading';
      })
      .addCase(linkTwitterAsync.fulfilled, (state) => {
        state.linkTwitter.status = 'success';
      })
      .addCase(linkTwitterAsync.rejected, (state) => {
        state.linkTwitter.status = 'failed';
      })
      .addCase(setPasswordByInvitationTokenAsync.pending, (state) => {
        state.setPasswordByInvitationToken.status = 'loading';
      })
      .addCase(setPasswordByInvitationTokenAsync.fulfilled, (state) => {
        state.setPasswordByInvitationToken.status = 'success';
      })
      .addCase(setPasswordByInvitationTokenAsync.rejected, (state) => {
        state.setPasswordByInvitationToken.status = 'failed';
      })
      .addCase(registerToBusinessGroupAsync.pending, (state) => {
        state.registerToBusinessGroup.status = 'loading';
      })
      .addCase(registerToBusinessGroupAsync.fulfilled, (state, action) => {
        state.registerToBusinessGroup = {
          status: 'idle',
        };
      })
      .addCase(registerToBusinessGroupAsync.rejected, (state) => {
        state.registerToBusinessGroup.status = 'failed';
      })
      .addCase(getUserCountryAsync.pending, (state) => {
        state.getCountry.status = 'loading';
      })
      .addCase(getUserCountryAsync.fulfilled, (state, action) => {
        state.getCountry = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getUserCountryAsync.rejected, (state) => {
        state.getCountry.status = 'failed';
      })
      .addCase(onboardingCompletedAsync.pending, (state) => {
        state.onboardingCompleted.status = 'loading';
      })
      .addCase(onboardingCompletedAsync.fulfilled, (state) => {
        state.onboardingCompleted.status = 'success';
      })
      .addCase(onboardingCompletedAsync.rejected, (state) => {
        state.onboardingCompleted.status = 'failed';
      })
      .addCase(addOnboardingQuestionnaireAsync.pending, (state) => {
        state.onboardingCompleted.status = 'loading';
      })
      .addCase(addOnboardingQuestionnaireAsync.fulfilled, (state) => {
        state.onboardingCompleted.status = 'success';
      })
      .addCase(addOnboardingQuestionnaireAsync.rejected, (state) => {
        state.onboardingCompleted.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
